import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useI18n } from "../i18n/I18nContext";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { Logo } from "./Logo";
import { LanguageToggle } from "./LanguageToggle";
import { ShoppingBag, Menu, X, User, LayoutDashboard } from "lucide-react";

export function PublicLayout({ children }) {
  const { t } = useI18n();
  const { user, isStaff, logout } = useAuth();
  const { count } = useCart();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();

  const links = [
    ["/", t("nav.home")], ["/services", t("nav.services")], ["/branches", t("nav.branches")],
    ["/gallery", t("nav.gallery")], ["/shop", t("nav.shop")], ["/memberships", t("nav.memberships")],
    ["/faq", t("nav.faq")], ["/contact", t("nav.contact")],
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <header className="sticky top-0 z-50 border-b border-border bg-white/85 backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-8">
          <Link to="/" data-testid="header-logo-link"><Logo height={38} /></Link>
          <nav className="hidden items-center gap-1 lg:flex">
            {links.map(([to, label]) => (
              <NavLink key={to} to={to} data-testid={`nav-${to === "/" ? "home" : to.slice(1)}`}
                className={({ isActive }) => `rounded-lg px-3 py-2 text-sm font-medium transition-colors ${isActive ? "text-primary" : "text-slate-600 hover:text-primary"}`}>
                {label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <LanguageToggle />
            <Link to="/cart" data-testid="cart-icon-link" className="relative rounded-full p-2 text-slate-700 hover:bg-slate-100">
              <ShoppingBag className="h-5 w-5" />
              {count > 0 && <span className="absolute -top-0.5 -end-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[hsl(var(--brand-red))] px-1 text-[10px] font-bold text-white">{count}</span>}
            </Link>
            {isStaff && <button onClick={() => nav("/admin")} data-testid="header-admin-btn" className="hidden rounded-full p-2 text-slate-700 hover:bg-slate-100 sm:block" title={t("nav.admin")}><LayoutDashboard className="h-5 w-5" /></button>}
            {user ? (
              <button onClick={() => nav(user.kind === "customer" ? "/account" : "/admin")} data-testid="header-account-btn" className="rounded-full p-2 text-slate-700 hover:bg-slate-100"><User className="h-5 w-5" /></button>
            ) : (
              <Link to="/login" data-testid="header-login-btn" className="hidden rounded-full border border-border px-3 py-1.5 text-sm font-medium text-slate-700 hover:border-primary hover:text-primary sm:inline-block">{t("nav.login")}</Link>
            )}
            <Link to="/booking" data-testid="header-book-btn" className="hidden rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm transition-all hover:bg-[hsl(var(--primary))]/90 active:scale-95 sm:inline-block">{t("nav.book")}</Link>
            <button onClick={() => setOpen((o) => !o)} data-testid="mobile-menu-btn" className="rounded-lg p-2 text-slate-700 lg:hidden">{open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}</button>
          </div>
        </div>
        {open && (
          <div className="border-t border-border bg-white px-4 py-3 lg:hidden" data-testid="mobile-menu">
            <div className="flex flex-col gap-1">
              {links.map(([to, label]) => (
                <NavLink key={to} to={to} onClick={() => setOpen(false)} className={({ isActive }) => `rounded-lg px-3 py-2.5 text-sm font-medium ${isActive ? "bg-primary/10 text-primary" : "text-slate-700"}`}>{label}</NavLink>
              ))}
              <Link to="/booking" onClick={() => setOpen(false)} className="mt-2 rounded-lg bg-primary px-3 py-2.5 text-center text-sm font-semibold text-white">{t("nav.book")}</Link>
              {!user && <Link to="/login" onClick={() => setOpen(false)} className="rounded-lg border border-border px-3 py-2.5 text-center text-sm font-medium">{t("nav.login")}</Link>}
              {user && <button onClick={() => { logout(); setOpen(false); }} className="rounded-lg px-3 py-2.5 text-start text-sm font-medium text-[hsl(var(--brand-red))]">{t("nav.logout")}</button>}
            </div>
          </div>
        )}
      </header>
      <main className="flex-1 pb-16 lg:pb-0">{children}</main>
      <Link to="/booking" data-testid="sticky-book-bar" className="fixed inset-x-0 bottom-0 z-40 flex items-center justify-center gap-2 py-3.5 text-sm font-bold text-white shadow-[0_-4px_20px_rgba(0,0,0,0.12)] lg:hidden" style={{ backgroundImage: "linear-gradient(90deg, hsl(var(--brand-blue)), hsl(var(--brand-orange)))" }}>
        <ShoppingBag className="h-4 w-4" /> {t("nav.book")}
      </Link>
      <Footer />
    </div>
  );
}

function Footer() {
  const { t } = useI18n();
  return (
    <footer className="mt-16 border-t border-border bg-[hsl(var(--neutral-dark,222_47%_11%))] bg-slate-900 text-slate-300">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-8 px-4 py-12 sm:px-8 md:grid-cols-4">
        <div className="md:col-span-2">
          <div className="rounded-lg bg-white p-2 inline-block"><Logo height={34} /></div>
          <p className="mt-4 max-w-md text-sm leading-relaxed text-slate-400">{t("footer.about")}</p>
        </div>
        <div>
          <h4 className="mb-3 text-sm font-semibold text-white">{t("footer.quick")}</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/services" className="hover:text-white">{t("nav.services")}</Link></li>
            <li><Link to="/branches" className="hover:text-white">{t("nav.branches")}</Link></li>
            <li><Link to="/gifts" className="hover:text-white">{t("nav.gifts")}</Link></li>
            <li><Link to="/booking" className="hover:text-white">{t("nav.book")}</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="mb-3 text-sm font-semibold text-white">{t("footer.legal")}</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/policies/privacy" className="hover:text-white">{t("footer.privacy")}</Link></li>
            <li><Link to="/policies/terms" className="hover:text-white">{t("footer.terms")}</Link></li>
            <li><Link to="/policies/cancellation" className="hover:text-white">{t("footer.cancellation")}</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-slate-800 py-4 text-center text-xs text-slate-500">
        © {new Date().getFullYear()} متوهج | Motawahj — {t("footer.rights")}
      </div>
    </footer>
  );
}
